import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeMenuPlacement, useMenuPlacement } from 'shared/components/ui/menuPlacement';

/**
 * FloatingMenu: the shell every hand-rolled dropdown panel should wear.
 *
 * Portaled to <body> and `position: fixed` against the trigger, so no
 * scrolling or overflow-hidden ancestor (a dialog body, a table) can clip it
 * or be made to grow by it. It stays inside the viewport, opens above or
 * below per the surrounding MenuPlacementProvider (or `placement`), caps its
 * height to the room available and scrolls its own content.
 *
 *   anchorRef   the trigger (or its wrapper) the menu hangs from
 *   open        render the menu
 *   onClose     outside click / Escape (Escape is swallowed, so it closes the
 *               menu and not the dialog the menu sits in)
 *   width       px; defaults to the trigger's width
 *   align       'left' | 'right': which trigger edge the menu lines up with
 *   maxHeight   px cap; the menu scrolls beyond it
 *   minHeight   room a preferred side must offer before the menu opens there (a big
 *               panel passes its natural height so it flips rather than squeezes)
 *   scroll      false when the children manage their own scrolling (a sticky
 *               search box over a scrolling list); the panel is then a flex column
 */
const MENU_Z = 1400;

export default function FloatingMenu({
  anchorRef, open, onClose, width, align = 'left', maxHeight = 280, minHeight, placement,
  scroll = true, className = '', style, children, ...rest
}) {
  const contextPlacement = useMenuPlacement();
  const prefer = placement || contextPlacement;
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = computeMenuPlacement(rect, { prefer, width, maxHeight, ...(minHeight ? { minHeight } : {}) });
    if (align === 'right' && width) next.left = Math.min(Math.max(8, rect.right - next.width), Math.max(8, window.innerWidth - next.width - 8));
    setPos(next);
  }, [anchorRef, prefer, width, maxHeight, minHeight, align]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    return undefined;
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    // The menu is fixed: a scroll anywhere else (the dialog body, the page) would strand it.
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) place(); };
    const onDoc = (e) => {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation(); onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place, onClose, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={menuRef}
      {...rest}
      className={`rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised,var(--surface-base))] shadow-token-lg ${scroll ? 'overflow-y-auto' : 'flex flex-col overflow-hidden'} ${className}`}
      style={{
        position: 'fixed', zIndex: MENU_Z, left: pos.left, top: pos.top, bottom: pos.bottom,
        width: pos.width, maxHeight: pos.maxHeight, fontFamily: 'Inter, sans-serif', ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
